// ContentConfig.tsx
import React, { useState } from 'react';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import Select from 'react-select';
import Input from '../../../forms/input.component';
import Title from '../../../forms/titles.component';
import TextArea from '../../../forms/text-area.component';
import Button from '../../../forms/button.component';
import Icon from '../../../forms/icon.component';
import InvalidFeedback from '../../../forms/invalid-feedback.component';
import { Dropzone } from '@dropzone-ui/react';
import { AlertService } from '../../../../services/alert.service';
import Tooltip from '../../../forms/tooltip.component';

const BUTTON_TYPE_OPTIONS = [
  { value: 'TEXT', label: 'Texto' },
  { value: 'URL', label: 'URL' }
];

export const ContentConfig: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext();
  const {
    fields: buttonFields,
    append: appendButton,
    remove: removeButton
  } = useFieldArray({
    control,
    name: 'interactive.buttons.action.sections.rows'
  });

  // const imgFile = watch('interactive.buttons.image.file');
  const imgPreview = watch('interactive.buttons.image.es');

  const type = watch('interactive.type');

  const urlRegex = /https?:\/\/.+/;

  const handleDrop = (files: any) => {
    const valid = files.find((f: any) => f.file);
    if (valid) {
      setValue('interactive.buttons.image.file', valid.file as File);
      setValue('interactive.buttons.image.es', URL.createObjectURL(valid.file as File));
    }
  };
  const [editingButtonIndex, setEditingButtonIndex] = useState<number | null>(null);

  const startAddButton = () => {
    appendButton({ type: '', es: '', url: '' });
    setEditingButtonIndex(buttonFields.length);
  };

  const cancelEditButton = () => {
    if (editingButtonIndex === null) return;

    const current = getValues(`interactive.buttons.action.sections.rows.${editingButtonIndex}`) as {
      type?: string;
      es?: string;
      url?: string;
    };

    const isNewBtn =
      !current.type &&
      (!current.es || current.es.trim() === '') &&
      (!current.url || current.url.trim() === '');

    if (isNewBtn) {
      removeButton(editingButtonIndex);
    }

    setEditingButtonIndex(null);
  };

  const saveEditButton = () => {
    setEditingButtonIndex(null);
  };

  const isValidContent =
    type === 'TEXT'
      ? watch('interactive.text.es')
      : type === 'BUTTONS'
        ? watch('interactive.buttons.body.es') && buttonFields.length > 0
        : false;

  return (
    <div
      className={`modal fade show card d-block ${type === 'TEXT' ? 'basic-interactive-modal' : 'content-interactive-modal'}`}
      data-blendy-to="nodeContentModal"
    >
      <div className="card-body position-relative pt-7">
        <div
          className="position-absolute top-0 end-0 m-2 btn btn-icon btn-icon-danger fs-2x"
          onClick={
            isValidContent && editingButtonIndex === null
              ? onClose
              : () => AlertService.toastWarning('Debes completar todos los campos antes de salir')
          }
        >
          <Icon icon="ki-cross" size="fs-2x" />
        </div>

        <Title
          title="Contenido del mensaje"
          subtitle="Configura el contenido del nodo"
          type="info"
          icon="ki-note"
        />

        {/* Si es TEXT */}
        {type === 'TEXT' && (
          <TextArea
            label="Mensaje"
            rows={4}
            requiredIndicator="required"
            {...register('interactive.text.es', {
              required: 'El texto es requerido',
              maxLength: { value: 1024, message: 'Máx. 1024 caracteres' }
            })}
            counter={1024}
            value={watch('interactive.text.es')?.length || 0}
            placeholder="Escribe el mensaje"
            errors={(errors.interactive as any)?.text?.es?.message}
          />
        )}

        {/* Si es BUTTONS */}
        {type === 'BUTTONS' && (
          <div className="container-fluid border-top pt-5">
            <div className="row">
              <div className="col-6 pe-8">
                <Input
                  label="Encabezado (opcional)"
                  placeholder="Agrega un encabezado"
                  currentLength={watch('interactive.buttons.header.es')?.length || 0}
                  maxLength={60}
                  {...register('interactive.buttons.header.es')}
                  errors={(errors.interactive as any)?.buttons?.header?.es?.message}
                />

                <TextArea
                  label="Mensaje"
                  rows={2}
                  requiredIndicator="required"
                  {...register('interactive.buttons.body.es', { required: 'Requerido' })}
                  counter={1024}
                  value={watch('interactive.buttons.body.es')?.length || 0}
                  errors={(errors.interactive as any)?.buttons?.body?.es?.message}
                />

                {/* Imagen opcional */}
                <div className="mb-3">
                  <label className="form-label">Imagen (opcional)</label>
                  <Controller
                    control={control}
                    name="interactive.buttons.image.file"
                    render={({ field: { onChange } }) =>
                      imgPreview ? (
                        <div className="text-center">
                          <img
                            src={imgPreview}
                            style={{ maxHeight: 120 }}
                            className="rounded mb-2"
                          />
                          <Button
                            type="button"
                            className="btn btn-sm btn-light-danger"
                            onClick={() => {
                              onChange(null);
                              setValue('interactive.buttons.image.es', '');
                            }}
                          >
                            Eliminar
                          </Button>
                        </div>
                      ) : (
                        <Dropzone
                          onChange={handleDrop}
                          accept="image/*"
                          maxFiles={1}
                          maxFileSize={5 * 1024 * 1024}
                          className="dropzone cursor-pointer"
                        >
                          <div className="dz-message text-center fs-5 d-flex flex-column align-items-center">
                            <Icon icon="ki-file-added" size="fs-5x" className="text-primary mb-2" />
                            <div className="align-middle">
                              Suelta o sube una imagen
                              <br />
                              (máx. 5 MB)
                            </div>
                          </div>
                        </Dropzone>
                      )
                    }
                  />
                  <InvalidFeedback
                    error={(errors.interactive as any)?.buttons?.image?.es && 'URL requerida'}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="card pb-3">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 d-flex align-items-center gap-2">
                      <Tooltip
                        title="Los botones se utilizan para enviar mensajes a tus clientes. Puedes agregar hasta 10 botones. Si agregas más de 3 botones, aparecerán en forma de lista y los dos primeros aparecerán en el mensaje."
                        className="width-fit-content d-inline-block cursor-pointer"
                      >
                        <Icon icon="ki-information-3" size="fs-1" type="info" />
                      </Tooltip>
                      Botones
                    </h6>

                    {editingButtonIndex === null && buttonFields.length < 10 && (
                      <Button
                        type="button"
                        className="btn btn-sm btn-light-primary"
                        onClick={startAddButton}
                      >
                        + Agregar
                      </Button>
                    )}
                  </div>
                  {editingButtonIndex !== null
                    ? (() => {
                        const i = editingButtonIndex ?? buttonFields.length;
                        const buttonText = watch(
                          `interactive.buttons.action.sections.rows.${i}.es`
                        );
                        const buttonType = watch(
                          `interactive.buttons.action.sections.rows.${i}.type`
                        );
                        const buttonUrl = watch(
                          `interactive.buttons.action.sections.rows.${i}.url`
                        );
                        const isValid =
                          buttonText &&
                          buttonType &&
                          (buttonType === 'URL' ? urlRegex.test(buttonUrl) : true);

                        return (
                          <div className="p-3 border rounded-4">
                            <label className="form-label">
                              Tipo de botón<span className="required"></span>
                            </label>
                            <Controller
                              control={control}
                              name={`interactive.buttons.action.sections.rows.${i}.type`}
                              rules={{ required: true }}
                              render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <>
                                  <Select
                                    options={BUTTON_TYPE_OPTIONS}
                                    noOptionsMessage={({ inputValue }) =>
                                      inputValue
                                        ? `Sin opciones para '${inputValue}'`
                                        : 'Sin opciones'
                                    }
                                    className={
                                      error ? 'form-select-custom is-invalid' : 'form-select-custom'
                                    }
                                    classNamePrefix="form-select-custom"
                                    placeholder="Selecciona tipo de boton"
                                    onChange={(
                                      selectedOption: {
                                        value: string;
                                        label: string;
                                      } | null
                                    ) => {
                                      onChange(selectedOption ? selectedOption.value : '');
                                    }}
                                    value={
                                      BUTTON_TYPE_OPTIONS.find(
                                        (option) => option.value === value
                                      ) || null
                                    }
                                  />
                                  <InvalidFeedback error={error} />
                                </>
                              )}
                            />
                            <Input
                              label="Etiqueta"
                              requiredIndicator="required"
                              {...register(
                                `interactive.buttons.action.sections.rows.${editingButtonIndex}.es` as const,
                                {
                                  required: 'Requerido',
                                  maxLength: { value: 24, message: 'Máx. 24 caracteres' }
                                }
                              )}
                              placeholder="Texto del botón"
                              currentLength={
                                watch(
                                  `interactive.buttons.action.sections.rows.${editingButtonIndex}.es`
                                )?.length || 0
                              }
                              errors={
                                (errors.interactive as any)?.buttons?.action?.sections?.rows?.[
                                  editingButtonIndex
                                ]?.es?.message
                              }
                            />

                            {watch(`interactive.buttons.action.sections.rows.${i}.type`) ===
                              'URL' && (
                              <>
                                <Input
                                  label="Enlace (URL)"
                                  requiredIndicator="required"
                                  placeholder="https://..."
                                  errors={
                                    (errors.interactive as any)?.buttons?.action?.sections?.rows?.[
                                      i
                                    ]?.url?.message
                                  }
                                  {...register(
                                    `interactive.buttons.action.sections.rows.${i}.url` as never,
                                    {
                                      required: true,
                                      pattern: {
                                        value: urlRegex,
                                        message: 'URL inválida'
                                      }
                                    }
                                  )}
                                />
                              </>
                            )}
                            <div className="mt-3 d-flex justify-content-end gap-2">
                              <Button
                                type="button"
                                className="btn btn-sm btn-light-secondary"
                                onClick={cancelEditButton}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={saveEditButton}
                                disabled={!isValid}
                              >
                                Guardar
                              </Button>
                            </div>
                          </div>
                        );
                      })()
                    : (() => {
                        const buttonValues: Array<{ type?: string; es?: string; url?: string }> =
                          watch('interactive.buttons.action.sections.rows') || [];

                        return (
                          <ul
                            className="list-group list-group-flush overflow-auto"
                            style={{ maxHeight: '30rem' }}
                          >
                            {buttonValues.length > 0 ? (
                              buttonValues.map((v, i) => (
                                <li
                                  key={i}
                                  className="list-group-item d-flex justify-content-between align-items-center "
                                >
                                  <div className="d-flex flex-column">
                                    <strong className="d-flex align-items-center gap-2 text-truncate mw-150px">
                                      Botón {i + 1}:{' '}
                                      {BUTTON_TYPE_OPTIONS.find((o) => o.value === v.type)?.label ||
                                        v.type}
                                      <Icon
                                        icon={v.type === 'URL' ? 'ki-fasten' : 'ki-password-check'}
                                        type={v.type === 'URL' ? 'primary' : 'info'}
                                        size="fs-4"
                                      />
                                    </strong>
                                    <small className="d-block text-truncate mw-150px">
                                      Mensaje: {v.es || '— sin mensaje —'}
                                      <br />
                                      {v.type === 'URL' && (
                                        <small className="text-muted">
                                          Enlace: {v.url || '— sin enlace —'}
                                        </small>
                                      )}
                                    </small>
                                  </div>
                                  <div className="btn-group btn-group-sm">
                                    <Button
                                      type="button"
                                      className="btn btn-light-warning"
                                      onClick={() => setEditingButtonIndex(i)}
                                    >
                                      <Icon icon="ki-pencil" />
                                    </Button>
                                    <Button
                                      type="button"
                                      className="btn btn-light-danger"
                                      onClick={() => removeButton(i)}
                                    >
                                      <Icon icon="ki-trash" />
                                    </Button>
                                  </div>
                                </li>
                              ))
                            ) : (
                              <li className="list-group-item text-center text-muted my-2">
                                No hay botones configurados
                              </li>
                            )}
                          </ul>
                        );
                      })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botón Listo */}
        <div className="text-end">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={onClose}
            disabled={!isValidContent || editingButtonIndex !== null}
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  );
};
